import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useParams, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { NavBarInfoActions } from '../../../Reducer/NavBarInfoReducer';
import { showSnackbar } from '../../../Reducer/SnackbarReducer';
import { Button } from '../../../Components/MUIs';
import BottomSheet from '../../../Components/Ui/BottomSheet';
import SignBottomSheet from '../../../Components/Ui/SignBottomSheet';
import useFileUpload from '../../../Hooks/useFileUpload';
import M_PEA_EDU_LOG_DETAIL from './M_PEA_EDU_LOG_DETAIL';
import { useSearchPeaEduLogDetail1 } from './M_PEA_EDU_LOG_DETAIL_Query';
import { SaveLogicPeaEduApprove1, SaveLogicPeaEduReject1 } from './M_PEA_EDU_LOG_DETAIL_Logic';

/**
 * 안전보건교육일지 상세 (기능단 NOT_BASICF)
 * 마스터: GetTraCustom02R1, 참석자: GetTraCustom02R2
 */
export default function M_PEA_EDU_LOG_DETAILF() {
  const { id } = useParams();
  const location = useLocation();
  const dispatch = useDispatch();
  const { userInfo } = useSelector((s) => s.BaseInfo);
  const listRow = location.state?.row;

  const signatureCanvas = useRef(null);
  const [signInputs, setSignInputs] = useState({ mapUrl: '', mapFiles: {} });
  const [signSheetOpen, setSignSheetOpen] = useState(false);
  const [approveSubmitting, setApproveSubmitting] = useState(false);
  const [rejectSheetOpen, setRejectSheetOpen] = useState(false);
  const [returnDetailText, setReturnDetailText] = useState('');
  const [rejectSubmitting, setRejectSubmitting] = useState(false);

  const signUpload = useFileUpload({
    oldFilePathField: 'signImagePath',
    newFileNameField: 'saveName',
    getTargetPath: (_item, custCd) => `TPA/${custCd || 'default'}/ApproverSign`,
  });

  const {
    detail,
    notFound,
    errorMessage,
    isLoading,
    isFetching,
    refetchMain,
    refetchAtt,
  } = useSearchPeaEduLogDetail1(id, listRow);

  const ResetSignSheet = useCallback(() => {
    signatureCanvas.current?.clear?.();
    signUpload.clearFiles();
    setSignInputs({ mapUrl: '', mapFiles: {} });
  }, [signUpload]);

  const HandleSheetDismiss = useCallback(() => {
    setSignSheetOpen(false);
    ResetSignSheet();
  }, [ResetSignSheet]);

  const HandleRejectSheetDismiss = useCallback(() => {
    setRejectSheetOpen(false);
    setReturnDetailText('');
  }, []);

  const HandleSignConfirm = useCallback(async () => {
    if (approveSubmitting) return;
    if (!signInputs.mapUrl) {
      dispatch(
        showSnackbar({
          message: '서명을 완료해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!userInfo?.userid) {
      dispatch(
        showSnackbar({
          message: '로그인 사용자 정보가 없습니다.',
          type: 'error',
        })
      );
      return;
    }
    setApproveSubmitting(true);
    try {
      const path = await signUpload.SaveLogicFile();
      if (!path) {
        dispatch(
          showSnackbar({
            message: '서명 파일을 저장하지 못했습니다.',
            type: 'error',
          })
        );
        return;
      }
      const res = await SaveLogicPeaEduApprove1({
        tpmId: id,
        signPath: path,
        userIdPlain: userInfo.userid,
      });
      const ok = res?.isSuccess === true || res?.IsSuccess === true;
      const msg = res?.message ?? res?.Message ?? '';
      if (!ok) {
        dispatch(
          showSnackbar({
            message: msg || '승인 처리에 실패했습니다.',
            type: 'error',
          })
        );
        return;
      }
      dispatch(
        showSnackbar({
          message: msg || '결재가 반영되었습니다.',
          type: 'success',
        })
      );
      setSignSheetOpen(false);
      ResetSignSheet();
      await Promise.all([refetchMain(), refetchAtt()]);
    } catch (e) {
      const data = e?.response?.data;
      const m =
        typeof data === 'string'
          ? data
          : data?.message ?? data?.Message ?? e?.message;
      dispatch(
        showSnackbar({
          message: m || '승인 요청 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      setApproveSubmitting(false);
    }
  }, [
    approveSubmitting,
    signInputs.mapUrl,
    userInfo?.userid,
    signUpload,
    dispatch,
    id,
    refetchMain,
    refetchAtt,
    ResetSignSheet,
  ]);

  const HandleRejectConfirm = useCallback(async () => {
    if (rejectSubmitting) return;
    const detailText = String(returnDetailText ?? '').trim();
    if (!detailText) {
      dispatch(
        showSnackbar({
          message: '반려 사유를 입력해 주세요.',
          type: 'warning',
        })
      );
      return;
    }
    if (!userInfo?.userid) {
      dispatch(
        showSnackbar({
          message: '로그인 사용자 정보가 없습니다.',
          type: 'error',
        })
      );
      return;
    }
    setRejectSubmitting(true);
    try {
      const res = await SaveLogicPeaEduReject1({
        tpmId: id,
        returnDetailPlain: detailText,
        userIdPlain: userInfo.userid,
      });
      const ok = res?.isSuccess === true || res?.IsSuccess === true;
      const msg = res?.message ?? res?.Message ?? '';
      if (!ok) {
        dispatch(
          showSnackbar({
            message: msg || '반려 처리에 실패했습니다.',
            type: 'error',
          })
        );
        return;
      }
      dispatch(
        showSnackbar({
          message: msg || '반려 처리되었습니다.',
          type: 'success',
        })
      );
      HandleRejectSheetDismiss();
      await Promise.all([refetchMain(), refetchAtt()]);
    } catch (e) {
      const data = e?.response?.data;
      const m =
        typeof data === 'string'
          ? data
          : data?.message ?? data?.Message ?? e?.message;
      dispatch(
        showSnackbar({
          message: m || '반려 요청 중 오류가 발생했습니다.',
          type: 'error',
        })
      );
    } finally {
      setRejectSubmitting(false);
    }
  }, [
    rejectSubmitting,
    returnDetailText,
    userInfo?.userid,
    dispatch,
    id,
    refetchMain,
    refetchAtt,
    HandleRejectSheetDismiss,
  ]);

  useEffect(() => {
    dispatch(NavBarInfoActions.setType('titleBar'));
    dispatch(NavBarInfoActions.setTitleName('안전보건교육일지'));
    dispatch(NavBarInfoActions.setBgColor('#FFF'));
  }, [dispatch]);

  const HandleReject = () => {
    if (detail?.approvers?.final?.done) return;
    setSignSheetOpen(false);
    ResetSignSheet();
    setRejectSheetOpen(true);
  };

  const HandleApprove = () => {
    if (detail?.approvers?.final?.done) return;
    setRejectSheetOpen(false);
    setReturnDetailText('');
    setSignSheetOpen(true);
  };

  const approveDisabled =
    approveSubmitting || Boolean(detail?.approvers?.final?.done);

  const rejectDisabled =
    rejectSubmitting ||
    approveSubmitting ||
    Boolean(detail?.approvers?.final?.done);

  if (isLoading) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-4 text-sm text-slate-500">
        교육일지 정보를 불러오는 중입니다…
      </div>
    );
  }

  if (errorMessage) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-4 text-center text-sm text-slate-600">
        데이터를 불러오지 못했습니다. 목록에서 다시 들어와 주세요.
      </div>
    );
  }

  if (notFound || !detail) {
    return (
      <div className="flex h-full flex-col items-center justify-center bg-slate-50 px-4 text-center text-sm text-slate-600">
        해당 교육일지를 찾을 수 없습니다. 조회 기간을 확인하거나 목록에서 다시
        선택해 주세요.
      </div>
    );
  }

  return (
    <>
      <div className="relative flex h-full flex-col bg-slate-50">
        {isFetching ? (
          <div className="absolute top-2 right-2 z-20 rounded-md bg-white/90 px-2 py-1 text-[10px] text-slate-500 shadow">
            갱신 중…
          </div>
        ) : null}
        <M_PEA_EDU_LOG_DETAIL
          detail={detail}
          onReject={HandleReject}
          onApprove={HandleApprove}
          approveDisabled={approveDisabled}
          rejectDisabled={rejectDisabled}
        />
      </div>
      {signSheetOpen ? (
        <SignBottomSheet
          signMode="registration"
          signatureCanvas={signatureCanvas}
          signInputs={signInputs}
          setSignInputs={setSignInputs}
          alarmData={[]}
          signUpload={signUpload}
          onSave={HandleSignConfirm}
          onDismiss={HandleSheetDismiss}
          confirmDisabled={approveSubmitting}
        />
      ) : null}
      {rejectSheetOpen ? (
        <BottomSheet
          onDismiss={HandleRejectSheetDismiss}
          isHeaderChildren
          headerText=""
          headerChildren={
            <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>
              교육일지&nbsp;
              <Typography component="span" sx={{ color: '#FF6161', fontWeight: 'bold' }}>
                반려
              </Typography>
            </Typography>
          }
        >
          <Box sx={{ px: 1 }}>
            <Typography sx={{ color: '#918A8A', pb: 1, fontSize: '14px' }}>
              반려 사유는 신청자에게 전달됩니다.
            </Typography>
            <Typography sx={{ fontSize: '16px', fontWeight: 'bold', pb: 0.5 }}>
              반려 사유
            </Typography>
            <textarea
              value={returnDetailText}
              onChange={(e) => setReturnDetailText(e.target.value)}
              maxLength={2000}
              rows={6}
              placeholder="반려 사유를 입력하세요"
              className="box-border w-full resize-none rounded-lg border-0 bg-[#F8F8F8] p-3.5 text-base outline-none"
            />
          </Box>
          <Box sx={{ p: 1, pt: 2 }}>
            <Button
              name="반려하기"
              sx={{
                width: '100%',
                height: 40,
                borderRadius: '10px',
                backgroundColor: '#FF6161',
                color: '#fff',
                boxShadow: 'none',
              }}
              disabled={rejectSubmitting}
              onClick={HandleRejectConfirm}
            />
          </Box>
        </BottomSheet>
      ) : null}
    </>
  );
}
